import { ClipboardDocumentIcon, PhoneIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * A phone number with Call, WhatsApp and Copy beside it.
 *
 * Used everywhere the dashboard shows a number, so it behaves the same on
 * every page. Call dials on a phone or tablet, or opens the computer's
 * calling app (such as Phone Link); WhatsApp opens a chat, which works on a
 * computer through WhatsApp Web; Copy is the fallback for a computer with no
 * calling app.
 *
 * Every click stops there, so a button inside a clickable card or row - like
 * the broadcast list, where clicking a person selects them - does only what
 * it says.
 */

const digitsOf = (phone) => String(phone || '').replace(/\D/g, '');

// Indian mobile numbers are stored as 10 digits; dialling and WhatsApp need
// the country code.
const withCountryCode = (digits) => (digits.length === 10 ? `91${digits}` : digits);

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2zm5.8 14.03c-.24.68-1.42 1.32-1.95 1.36-.5.05-1.13.07-1.82-.11a16.6 16.6 0 0 1-1.65-.61c-2.9-1.25-4.79-4.17-4.94-4.36-.14-.19-1.18-1.57-1.18-3s.75-2.13 1.02-2.42c.27-.29.58-.36.78-.36h.56c.18 0 .42-.07.66.5.24.58.83 2.01.9 2.15.07.15.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.29.75 1.24 1.61 2 1.11.99 2.04 1.3 2.33 1.44.29.15.46.12.63-.07.17-.19.73-.85.92-1.14.19-.29.39-.24.66-.15.27.1 1.7.8 1.99.95.29.15.48.22.56.34.07.12.07.7-.17 1.38z" />
  </svg>
);

const SIZES = {
  sm: { btn: 'w-7 h-7', icon: 'w-4 h-4', text: 'text-sm' },
  md: { btn: 'w-8 h-8', icon: 'w-[18px] h-[18px]', text: 'text-sm' },
};

const PhoneActions = ({ phone, size = 'sm', showNumber = true, empty = 'N/A', className = '', textClassName = '' }) => {
  const digits = digitsOf(phone);

  if (!digits) {
    return <span className={`${SIZES[size].text} text-gray-400 ${textClassName}`}>{empty}</span>;
  }

  const full = withCountryCode(digits);
  const s = SIZES[size] || SIZES.sm;
  const stop = (e) => e.stopPropagation();

  const copy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(String(phone).trim());
      toast.success('Number copied');
    } catch (err) {
      toast.error('Could not copy - select the number instead');
    }
  };

  const btn = `inline-flex items-center justify-center ${s.btn} rounded-full border transition-colors`;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} onClick={stop}>
      {showNumber ? (
        <span className={`${s.text} font-medium text-gray-900 select-all ${textClassName}`}>{phone}</span>
      ) : null}
      <a
        href={`tel:+${full}`}
        onClick={stop}
        title={`Call ${phone}`}
        aria-label={`Call ${phone}`}
        className={`${btn} border-green-200 bg-green-50 text-green-700 hover:bg-green-100`}
      >
        <PhoneIcon className={s.icon} />
      </a>
      <a
        href={`https://wa.me/${full}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={stop}
        title={`WhatsApp ${phone}`}
        aria-label={`WhatsApp ${phone}`}
        className={`${btn} border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100`}
      >
        <WhatsAppIcon className={s.icon} />
      </a>
      <button
        type="button"
        onClick={copy}
        title="Copy number"
        aria-label={`Copy ${phone}`}
        className={`${btn} border-gray-200 bg-white text-gray-500 hover:bg-gray-50`}
      >
        <ClipboardDocumentIcon className={s.icon} />
      </button>
    </span>
  );
};

export default PhoneActions;
